import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, ArrowUp, ArrowDown, Pencil, X } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { uploadImage } from "../../lib/upload";

const EMPTY = { title: "", image_url: "", image_url_mobile: "", link: "", placement: "hero", active: true, start_date: "", end_date: "" };

export default function BannersTab() {
  const [banners, setBanners] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const fileMobileRef = useRef();

  const load = () => api.get("/admin/banners").then((r) => setBanners(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (fn) => {
    try { const res = await fn(); toast.success(res.data.message); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const onFile = async (e, field = "image_url") => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { const url = await uploadImage(f); setForm((s) => ({ ...s, [field]: url })); toast.success("Image uploaded"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const save = () => {
    if (!form.image_url) return toast.error("Upload a banner image first");
    act(() => editId ? api.put(`/admin/banners/${editId}`, form) : api.post("/admin/banners", form));
    setForm(EMPTY); setEditId(null);
  };

  const move = (idx, dir) => {
    const ids = banners.map((b) => b.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    act(() => api.put("/admin/banners/reorder", { ids }));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div className="space-y-4">
        {banners.length === 0 && <p className="text-sm text-slate-500">No sponsored banners yet.</p>}
        {banners.map((b, idx) => (
          <div key={b.id} data-testid={`admin-banner-${b.id}`} className="nb p-4 flex gap-4 items-start">
            <img src={b.image_url} alt="" className="w-28 h-16 object-cover rounded-lg border-2 border-[#171B33] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-extrabold truncate">{b.title || "(untitled)"}</p>
              <p className="text-xs text-slate-500 mt-1 capitalize">{b.placement === "hero" ? "Carousel" : "Strip"} · {b.active ? "Active" : "Inactive"}
                {(b.start_date || b.end_date) && ` · ${b.start_date || "…"} → ${b.end_date || "…"}`}</p>
              <p data-testid={`banner-stats-${b.id}`} className="text-xs font-bold mt-1">
                <span className="nb-badge bg-[#93C5FD]" style={{ padding: "2px 8px" }}>{b.views || 0} views</span>{" "}
                <span className="nb-badge bg-[#A3E635]" style={{ padding: "2px 8px" }}>{b.clicks || 0} clicks</span>{" "}
                <span className="text-slate-500 font-semibold">CTR {b.views ? ((100 * (b.clicks || 0)) / b.views).toFixed(1) : "0.0"}%</span>
              </p>
              {b.link && <p className="text-xs text-slate-400 truncate">{b.link}</p>}
            </div>
            <div className="flex flex-col gap-1.5 text-slate-400">
              <button data-testid={`banner-up-${b.id}`} onClick={() => move(idx, -1)} className="hover:text-[#171B33]"><ArrowUp className="w-4 h-4" /></button>
              <button data-testid={`banner-down-${b.id}`} onClick={() => move(idx, 1)} className="hover:text-[#171B33]"><ArrowDown className="w-4 h-4" /></button>
              <button data-testid={`banner-edit-${b.id}`} onClick={() => { setEditId(b.id); setForm({ ...EMPTY, ...b }); }} className="hover:text-[#171B33]"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`banner-delete-${b.id}`} onClick={() => window.confirm("Delete this banner?") && act(() => api.delete(`/admin/banners/${b.id}`))} className="hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="nb p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-extrabold">{editId ? "Edit Banner" : "Add Sponsored Banner"}</h3>
          {editId && <button data-testid="banner-cancel-edit" onClick={() => { setEditId(null); setForm(EMPTY); }} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
        </div>
        <div className="space-y-3">
          <div>
            <label className="nb-label">Banner image — desktop</label>
            <div className="mt-1.5 flex items-center gap-3">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="banner-file-input" />
              <button data-testid="banner-upload-btn" disabled={busy} onClick={() => fileRef.current.click()} className="nb-btn nb-btn-sm"><Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload desktop"}</button>
              {form.image_url && <img src={form.image_url} alt="" className="h-12 w-24 object-cover rounded-lg border-2 border-[#171B33]" />}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Carousel: <b>1600×600 px</b> · Strip: <b>1600×300 px</b></p>
            <label className="nb-label mt-3 block">Banner image — mobile (optional)</label>
            <div className="mt-1.5 flex items-center gap-3">
              <input ref={fileMobileRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "image_url_mobile")} data-testid="banner-file-mobile-input" />
              <button data-testid="banner-upload-mobile-btn" disabled={busy} onClick={() => fileMobileRef.current.click()} className="nb-btn nb-btn-sm"><Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload mobile"}</button>
              {form.image_url_mobile && <img src={form.image_url_mobile} alt="" className="h-12 w-24 object-cover rounded-lg border-2 border-[#171B33]" />}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">Carousel: <b>800×400 px</b> · Strip: <b>800×200 px</b>. Falls back to desktop image if empty.</p>
          </div>
          <div><label className="nb-label">Title (optional)</label>
            <input data-testid="banner-title-input" value={form.title} onChange={set("title")} className="nb-input mt-1.5" /></div>
          <div><label className="nb-label">Link (optional)</label>
            <input data-testid="banner-link-input" value={form.link} onChange={set("link")} placeholder="https://" className="nb-input mt-1.5" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="nb-label">Placement</label>
              <select data-testid="banner-placement-select" value={form.placement} onChange={set("placement")} className="nb-input mt-1.5">
                <option value="hero">Carousel (below hero)</option>
                <option value="strip">Strip (lower)</option>
              </select></div>
            <div><label className="nb-label">Start date</label>
              <input data-testid="banner-start-input" type="date" value={form.start_date} onChange={set("start_date")} className="nb-input mt-1.5" /></div>
            <div><label className="nb-label">End date</label>
              <input data-testid="banner-end-input" type="date" value={form.end_date} onChange={set("end_date")} className="nb-input mt-1.5" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input data-testid="banner-active-checkbox" type="checkbox" checked={form.active} onChange={set("active")} /> Active
          </label>
          <button data-testid="banner-save-btn" onClick={save} className="nb-btn nb-btn-lime">{editId ? "Save changes" : "Add Banner"}</button>
        </div>
      </div>
    </div>
  );
}
